import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import type {
  TooltipContentProps,
  TooltipRootProps,
} from "@kobalte/core/tooltip";
import { Tooltip as TooltipPrimitive } from "@kobalte/core/tooltip";
import { mergeProps, splitProps, type ValidComponent } from "solid-js";
import { cn } from "@/libs/cn";

export const TooltipTrigger = TooltipPrimitive.Trigger;

export const Tooltip = (props: TooltipRootProps) => {
  const merge = mergeProps<TooltipRootProps[]>({ gutter: 4 }, props);

  return <TooltipPrimitive {...merge} />;
};

type tooltipContentProps<T extends ValidComponent = "div"> =
  TooltipContentProps<T> & {
    class?: string;
  };

/**
 * The content caps at the space the popper has left in the viewport and scrolls
 * inside it. There is no separate body element here — a tooltip is a single box —
 * so the cap and the scrolling sit on the same element that receives
 * `local.class`, and a call site passing its own `max-h-*` replaces the cap
 * through `twMerge` instead of composing with it. None does today.
 *
 * Under reduced motion the scale is neutralised to 1 rather than the whole
 * animation being dropped: the fade still carries "something appeared", only the
 * zoom goes. It has to be the stacked `motion-reduce:data-*` form — a bare
 * `motion-reduce:` utility loses the cascade to `data-expanded:zoom-in-95`.
 */
export const TooltipContent = <T extends ValidComponent = "div">(
  props: PolymorphicProps<T, tooltipContentProps<T>>,
) => {
  const [local, rest] = splitProps(props as tooltipContentProps, ["class"]);

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        class={cn(
          "z-50 max-h-(--kb-popper-content-available-height) overflow-x-hidden overflow-y-auto rounded-md bg-card px-3 py-1.5 text-xs text-card-foreground data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0 data-closed:zoom-out-95 data-expanded:zoom-in-95 motion-reduce:data-closed:zoom-out-[1] motion-reduce:data-expanded:zoom-in-[1] shadow-md origin-(--kb-tooltip-content-transform-origin)",
          local.class,
        )}
        {...rest}
      />
    </TooltipPrimitive.Portal>
  );
};
